import { isEmpty } from 'lodash';

import type { SortOrder, WaitingAssetsSortField } from '@nova/core';
import type { InvalidateOptions } from '@tanstack/query-core';

export interface WaitingAssetsListQueryParams {
  sortField: WaitingAssetsSortField;
  sortOrder: SortOrder;
  arrivedWithinDays: number;
}

/**
 * Centralized query keys for presenter composables based on TanStack Query queries.
 * Used for cache invalidation and query deduplication.
 */
export const DEFAULT_INVALIDATE_OPTIONS: InvalidateOptions = {
  cancelRefetch: false
};

export const queryKeys = {
  all: ['yard'] as const,
  carrierAll: ['carrier'] as const,
  carrier: (carrierId: string) => [...queryKeys.carrierAll, carrierId] as const,
  carrierLookup: (lookup: { carrierId?: string | null; companyId?: string | null }) =>
    [...queryKeys.carrierAll, 'lookup', lookup] as const,
  carrierLists: () => [...queryKeys.carrierAll, 'list'] as const,
  carrierListsForOrg: (orgId: string | null) => [...queryKeys.carrierLists(), orgId] as const,
  carrierList: (orgId: string | null, warehouseId: string | null, search: string) =>
    [...queryKeys.carrierListsForOrg(orgId), warehouseId, search] as const,

  companyAll: ['company'] as const,
  companyLists: () => [...queryKeys.companyAll, 'list'] as const,
  companyList: (search: string) => [...queryKeys.companyLists(), search] as const,

  docks: ['docks'] as const,
  dock: (dockId: string) => [...queryKeys.docks, dockId] as const,

  // Warehouses
  warehouses: () => [...queryKeys.all, 'warehouses'] as const,
  /** List cache is split by restriction fingerprint (`all` or sorted joined ids). */
  warehousesList: (restrictionFingerprint = 'all') =>
    [...queryKeys.warehouses(), 'list', restrictionFingerprint] as const,
  warehouse: (warehouseId: string) => [...queryKeys.warehouses(), warehouseId] as const,

  // Spots
  spots: () => [...queryKeys.all, 'spots'] as const,
  spotsList: (warehouseId: string) => [...queryKeys.spots(), 'list', warehouseId] as const,

  spot: (spotId: string) => [...queryKeys.all, 'spot', spotId] as const,
  spotDetail: (spotId: string) => [...queryKeys.spot(spotId), 'detail'] as const,
  spotSummary: (spotId: string) => [...queryKeys.spot(spotId), 'summary'] as const,
  spotEvents: (spotId: string) => [...queryKeys.spot(spotId), 'events'] as const,

  // Spot Assignments
  spotAssignments: (spotId: string) => [...queryKeys.spot(spotId), 'assignments'] as const,
  spotAssignment: (assignmentId: string) =>
    [...queryKeys.all, 'spotAssignment', assignmentId] as const,

  // Asset Visits
  assetVisits: () => [...queryKeys.all, 'assetVisits'] as const,
  assetVisitsList: (warehouseId: string) =>
    [...queryKeys.assetVisits(), 'list', warehouseId] as const,
  waitingAssetsList: (warehouseId: string, params?: WaitingAssetsListQueryParams) =>
    [...queryKeys.assetVisits(), 'waiting', warehouseId, ...(params ? [params] : [])] as const,
  assetVisit: (visitId: string) => [...queryKeys.assetVisits(), visitId] as const,
  assetVisitDockedSummary: (visitId: string) =>
    [...queryKeys.assetVisit(visitId), 'dockedSummary'] as const,
  assetVisitArrivalContainers: (visitId: string) =>
    [...queryKeys.assetVisit(visitId), 'arrivalContainers'] as const,

  // Check-ins of the last 24 hours (arrivals drawer and top nav bell)
  recentArrivals: (warehouseId: string) =>
    [...queryKeys.assetVisits(), 'recentArrivals', warehouseId] as const,

  // Last driver message per thread (keyed by the sorted thread ids)
  messageThreadsUnreadDetails: (threadIdsFingerprint: string) =>
    [...queryKeys.all, 'messageThreads', 'unreadDetails', threadIdsFingerprint] as const,

  // Unread driver message counts (BFF: warehouse-scoped, keyed by visit; drives the chat badge)
  unreadMessageCounts: (warehouseId: string) =>
    [...queryKeys.assetVisits(), 'unreadCounts', warehouseId] as const,

  // Asset Containers
  assetContainers: () => [...queryKeys.all, 'assetContainers'] as const,
  assetContainer: (containerId: string) => [...queryKeys.assetContainers(), containerId] as const,

  // Selectable assets list (BFF: standalone containers with their open move/check tasks)
  assetRowsList: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'rows', warehouseId] as const,

  // Missing assets list (BFF: standalone containers whose latest status is Missing)
  missingAssetsList: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'missing', warehouseId] as const,

  // In transit assets list (BFF: containers whose latest status is In Transit)
  inTransitAssetsList: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'in-transit', warehouseId] as const,

  // Yard inventory check (the "open inventory" BFF, keyed by the task it resolves)
  yardInventoryChecks: () => [...queryKeys.assetContainers(), 'yardInventoryCheck'] as const,
  yardInventoryCheck: (taskId: string) => [...queryKeys.yardInventoryChecks(), taskId] as const,

  // Yard inventory counts (warehouse-scoped container counts for the check-task picker + cards)
  yardInventoryCounts: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'yardInventoryCounts', warehouseId] as const,

  // Yard Tasks
  task: (taskId: string) => [...queryKeys.all, 'task', taskId] as const,
  tasksByRelatedEntityList: (
    relatedEntity: string,
    relatedIdsFingerprint: string,
    statusesFingerprint: string,
    type: string
  ) =>
    [
      ...queryKeys.all,
      'tasks',
      'byRelatedEntity',
      relatedEntity,
      relatedIdsFingerprint,
      statusesFingerprint,
      type
    ] as const,
  tasksByStatusList: (status: string, warehouseFingerprint: string, filtersFingerprint: string) =>
    [
      ...queryKeys.all,
      'tasks',
      'byStatus',
      status,
      warehouseFingerprint,
      filtersFingerprint
    ] as const,
  yardTaskFilterOptions: (warehouseFingerprint: string, windowFingerprint: string) =>
    [...queryKeys.all, 'tasks', 'filterOptions', warehouseFingerprint, windowFingerprint] as const,
  yardDriverOptions: (warehouseId: string) =>
    [...queryKeys.all, 'tasks', 'driverOptions', warehouseId] as const,

  // Appointments
  appointment: (appointmentId: string) => [...queryKeys.all, 'appointment', appointmentId] as const,

  // Load types
  loadType: (loadTypeId: string) => [...queryKeys.all, 'loadType', loadTypeId] as const,

  // Spot Areas (Yard Areas)
  spotAreas: () => [...queryKeys.all, 'spotAreas'] as const,
  spotAreasList: (warehouseId: string) => [...queryKeys.spotAreas(), 'list', warehouseId] as const,
  spotArea: (areaId: string) => [...queryKeys.all, 'spotArea', areaId] as const,
  spotAreaDetail: (areaId: string) => [...queryKeys.spotArea(areaId), 'detail'] as const,

  // BFF View endpoints (pre-computed, flat data for specific views)
  bff: () => [...queryKeys.all, 'bff'] as const,
  listViewRows: (warehouseId: string, includeWaiting = true) =>
    [...queryKeys.bff(), 'listViewRows', warehouseId, includeWaiting] as const,
  spotGrid: (warehouseId: string) => [...queryKeys.bff(), 'spotGrid', warehouseId] as const,

  /** Custom form data presenter cache (object + trigger-set fingerprint). */
  customFormDataPresenter: (objectId: string, triggerIdsFingerprint: string) =>
    [...queryKeys.all, 'customFormData', 'presenter', objectId, triggerIdsFingerprint] as const
} as const;

export const taskKeys = {
  all: ['tasks'] as const,
  list: (queryArgs?: unknown) => {
    return [...taskKeys.all, 'list', ...(isEmpty(queryArgs) ? [] : [queryArgs])] as const;
  },
  detail: (taskId: string) => {
    return [...taskKeys.all, 'detail', taskId] as const;
  }
};

export const documentKeys = {
  all: ['documents'] as const,
  /** Omit `page` to key — or invalidate — every page of an entity's documents at once. */
  list: (relatedEntity: string, relatedId: string, page?: { limit: number; offset: number }) => {
    return [
      ...documentKeys.all,
      'list',
      relatedEntity,
      relatedId,
      ...(isEmpty(page) ? [] : [page])
    ] as const;
  }
};
